import React, { useState, useEffect } from 'react';
import { BarChart3, Users, Building2, Calendar, Download, Filter } from 'lucide-react';
import { CollaboratorReport, ProjectReport, PendingInvoicesReport } from '../../types/collaborators';
import { reportsService } from '../../services/supabaseService';
import { formatCurrency } from '../../utils/textUtils';

const CollaboratorsReports: React.FC = () => {
  const [activeReport, setActiveReport] = useState<'collaborators' | 'projects' | 'pending'>('collaborators');
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');
  const [collaboratorReports, setCollaboratorReports] = useState<CollaboratorReport[]>([]);
  const [projectReports, setProjectReports] = useState<ProjectReport[]>([]);
  const [pendingInvoices, setPendingInvoices] = useState<PendingInvoicesReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReports();
  }, [period]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Cargando reportes desde Supabase...');

      const [collabReports, projReports, pendingInv] = await Promise.all([
        reportsService.getCollaboratorReports(period),
        reportsService.getProjectReports(period),
        reportsService.getPendingInvoices()
      ]);

      setCollaboratorReports(collabReports);
      setProjectReports(projReports);
      setPendingInvoices(pendingInv);

      console.log('✅ Reportes cargados:', {
        colaboradores: collabReports.length,
        proyectos: projReports.length,
        facturasPendientes: pendingInv.length
      });
    } catch (error) {
      console.error('❌ Error loading reports:', error);
      setError('Error al cargar los reportes. Por favor, intenta de nuevo.');
      setCollaboratorReports([]);
      setProjectReports([]);
      setPendingInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    const { label } = reportsService.getDateRange(period);
    return label;
  };

  const exportToCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${getPeriodLabel().replace(/ /g, '_')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Generando reportes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 font-medium">{error}</p>
        <button
          onClick={loadReports}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Reportes y Análisis</h2>
          <p className="text-gray-600">Análisis de costes y rendimiento de colaboradores</p>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="month">Este mes</option>
              <option value="quarter">Este trimestre</option>
              <option value="year">Este año</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'collaborators', label: 'Por Colaborador', icon: Users },
              { id: 'projects', label: 'Por Proyecto', icon: Building2 },
              { id: 'pending', label: 'Facturas Pendientes', icon: Calendar }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveReport(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeReport === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeReport === 'collaborators' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Gasto por Colaborador - {getPeriodLabel()}
                </h3>
                <button
                  onClick={() => exportToCSV(collaboratorReports, 'colaboradores')}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
                  disabled={collaboratorReports.length === 0}
                >
                  <Download className="w-4 h-4" />
                  <span>Exportar CSV</span>
                </button>
              </div>

              {collaboratorReports.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Colaborador
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Horas
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Importe Total
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tarifa Promedio
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {collaboratorReports.map((report) => (
                          <tr key={(report as any).userId || (report as any).collaboratorId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                  {(report as any).userName || (report as any).collaboratorName}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{report.totalHours}h</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-900">
                                {report.totalAmount.toLocaleString()} {report.currency}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">
                                {(report.totalAmount / report.totalHours).toFixed(2)} {report.currency}/h
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-blue-600">Total Horas</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {collaboratorReports.reduce((sum, r) => sum + r.totalHours, 0)}h
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-green-600">Gasto Total</p>
                      <p className="text-2xl font-bold text-green-900">
                        {collaboratorReports.reduce((sum, r) => sum + r.totalAmount, 0).toLocaleString()}€
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-600">Plantilla Activos</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {collaboratorReports.length}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos disponibles</h3>
                  <p className="text-gray-500">No se encontraron partes de horas aprobados para el período seleccionado.</p>
                </div>
              )}
            </div>
          )}

          {activeReport === 'projects' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Gasto por Proyecto - {getPeriodLabel()}
                </h3>
                <button
                  onClick={() => exportToCSV(projectReports, 'proyectos')}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
                  disabled={projectReports.length === 0}
                >
                  <Download className="w-4 h-4" />
                  <span>Exportar CSV</span>
                </button>
              </div>

              {projectReports.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Proyecto
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cliente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Horas
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Importe Total
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Plantilla
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {projectReports.map((report) => (
                          <tr key={report.projectId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <Building2 className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                  {report.projectName}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{report.clientName}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{report.totalHours}h</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-900">
                                {report.totalAmount.toLocaleString()}€
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{(report as any).teamMembersCount || (report as any).collaboratorsCount || 0}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-blue-600">Proyectos Activos</p>
                      <p className="text-2xl font-bold text-blue-900">{projectReports.length}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-green-600">Gasto Total</p>
                      <p className="text-2xl font-bold text-green-900">
                        {projectReports.reduce((sum, r) => sum + r.totalAmount, 0).toLocaleString()}€
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-600">Horas Totales</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {projectReports.reduce((sum, r) => sum + r.totalHours, 0)}h
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos disponibles</h3>
                  <p className="text-gray-500">No se encontraron proyectos con partes de horas para el período seleccionado.</p>
                </div>
              )}
            </div>
          )}

          {activeReport === 'pending' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Facturas Pendientes de Pago</h3>
                <button
                  onClick={() => exportToCSV(pendingInvoices, 'facturas_pendientes')}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
                  disabled={pendingInvoices.length === 0}
                >
                  <Download className="w-4 h-4" />
                  <span>Exportar CSV</span>
                </button>
              </div>

              {pendingInvoices.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Factura
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Colaborador
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Importe
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Días Pendiente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estado
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingInvoices.map((invoice) => (
                          <tr key={invoice.invoiceId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-900">
                                {invoice.invoiceNumber}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900">{(invoice as any).userName || (invoice as any).collaboratorName}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(invoice.amount, invoice.currency)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`text-sm font-medium ${
                                invoice.daysOverdue > 7 ? 'text-red-600' :
                                invoice.daysOverdue > 3 ? 'text-yellow-600' : 'text-gray-900'
                              }`}>
                                {invoice.daysOverdue} días
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                {invoice.status === 'received' ? 'Recibida' : 'Validada'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-red-600">Facturas Pendientes</p>
                      <p className="text-2xl font-bold text-red-900">{pendingInvoices.length}</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-yellow-600">Importe Pendiente</p>
                      <p className="text-2xl font-bold text-yellow-900">
                        {pendingInvoices.reduce((sum, i) => sum + i.amount, 0).toLocaleString()}€
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay facturas pendientes</h3>
                  <p className="text-gray-500">¡Excelente! Todas las facturas están al día.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaboratorsReports;
